import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { Button } from '@/components/atoms'
import { GuestInput } from '@/models/guest-input'
import { EditForm } from '@/components/molecules/customer/edit-form'
import { FormProvider, useForm } from 'react-hook-form'
import { CustomerType } from '@/models/customer-manager/customer'
import { convertToSeparatedName } from '@/libs/convertToSeparatedName'
import { LoaderContextCreator } from '@/contexts/loader'
import { useErrorHandler } from '@/hooks/use-error-handler'
import * as api from '@/apis/aipass'
import dayjs from 'dayjs'

type EditCustomerInfoModalProps = {
  customer: CustomerType
  onCancel: () => void
  onSave: () => void
}

export const EditCustomerInfoModal: React.FC<EditCustomerInfoModalProps> = props => {
  const { t } = useTranslation()
  const { setIsLoading } = useContext(LoaderContextCreator())
  const { errorHandler } = useErrorHandler()

  const [customerFirstName, customerLastName] = convertToSeparatedName({ name: props.customer.name || '' })
  const [customerFirstNameKana, customerLastNameKana] = convertToSeparatedName({ name: props.customer.nameKana || '' })

  const formReturn = useForm<GuestInput>({
    defaultValues: {
      nationality: props.customer.nationality,
      lastName: customerLastName || '',
      firstName: customerFirstName || '',
      lastNameKana: customerLastNameKana || '',
      firstNameKana: customerFirstNameKana || '',
      birthDate: props.customer.birthDate || '',
      gender: props.customer.gender || '',
      address: props.customer.address || '',
      postalCode: props.customer.postalCode || '',
      telephone: props.customer.telephone || '',
      email: props.customer.email || '',
      occupation: props.customer.occupation || '',
      company: props.customer.company || '',
      otherOccupation: props.customer.otherOccupation || '',
      deliveryAgreementStatus: props.customer.deliveryAgreementStatus || 'unconfirmed',
    },
  })

  const onSave = async (formValue: GuestInput) => {
    setIsLoading(true)
    try {
      const requestParams = {
        ...formValue,
        name: formValue.lastName || formValue.firstName ? `${formValue.lastName} ${formValue.firstName}` : '',
        nameKana: formValue.lastNameKana || formValue.firstNameKana ? `${formValue.lastNameKana} ${formValue.firstNameKana}` : '',
        birthDate: formValue.birthDate ? dayjs(formValue.birthDate).format('YYYY-MM-DD') : null,
        telephone: formValue.telephone ? formValue.telephone : '0',
        picture: props.customer.picture,
        hotelCuicinUserId: props.customer.id,
      }
      await api.updateCustomerInfo(requestParams)

      props.onSave()
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <FormProvider {...formReturn}>
      <div css={modalWrapperStyle}>
        <div css={modalContentStyle}>
          <div css={modalTitleWrapperStyle}>
            <p css={modalTitleStyle}>{t('Edit customer information')}</p>
          </div>

          <div css={addGuestInfoWrapperStyle}>
            <div css={addGuestInfoStyle}>
              <EditForm />
            </div>
          </div>

          <div css={footerButtonWrapperStyle}>
            <Button width={110} height={38} marginRight={16} buttonType={3} onClick={props.onCancel}>
              {t('Cancel')}
            </Button>
            <Button width={110} height={38} buttonType={1} onClick={formReturn.handleSubmit(onSave)}>
              {t('Save')}
            </Button>
          </div>
        </div>
      </div>
    </FormProvider>
  )
}

const modalWrapperStyle = css({
  boxShadow: '0px 0px 6px #0000001A',
  border: '1px solid #F2F2F2',
  borderRadius: 5,
  marginRight: 16,
  marginBottom: 16,
  width: '100vw',
  height: '100vh',
  background: 'rgba(39, 39, 39, 0.5)',
  position: 'fixed',
  top: 0,
  left: 0,
  zIndex: 103,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
})

const modalContentStyle = css({
  width: 600,
  minHeight: 650,
  background: '#fff',
  borderRadius: 5,
  marginBottom: 40,
})

const modalTitleWrapperStyle = css({
  width: 'inherit',
  paddingLeft: 32,
  position: 'absolute',
  'z-index': '10000',
  backgroundColor: 'white',
  borderRadius: '5px 5px 0px 0px',
})

const modalTitleStyle = css({
  color: '#272727',
  fontSize: 18,
  fontWeight: 'bold',
  lineHeight: '60px',
  letterSpacing: '0.9px',
})

const addGuestInfoWrapperStyle = css({
  padding: '24px 32px',
  background: '#f2f2f2',
  display: 'flex',
  justifyContent: 'space-between',
  maxHeight: 650,
  width: 600,
  overflow: 'scroll',
  borderRadius: '5px 5px 0px 0px',
})

const footerButtonWrapperStyle = css({
  height: 60,
  paddingRight: 32,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  width: 'inherit',
  position: 'absolute',
  'z-index': '10000',
  backgroundColor: 'white',
  borderRadius: '0px 0px 5px 5px',
})

const addGuestInfoStyle = css({
  width: '100%',
  padding: 32,
  background: '#fff',
  border: '1px solid #ccc',
  borderRadius: 5,
  height: 'fit-content',
  marginTop: 52,
})
